import { useEffect, useMemo, useState } from "react";

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

export enum ReceiptBucket {
  PENDING_REVIEW = "pending_review",
  MATCHED = "matched",
  UNMATCHED = "unmatched",
}

export enum ReceiptSort {
  MOST_RECENT = "most_recent",
  OLDEST_FIRST = "oldest_first",
  CONFIDENCE = "confidence",
  HIGHEST_AMOUNT = "highest_amount",
  LOWEST_AMOUNT = "lowest_amount",
}

const VALID_RECEIPT_SORTS = new Set<string>(Object.values(ReceiptSort));

/** Parses a `sort` URL param, falling back to the default for anything unrecognized. */
export const parseReceiptSort = (raw: string | null): ReceiptSort =>
  raw && VALID_RECEIPT_SORTS.has(raw)
    ? (raw as ReceiptSort)
    : ReceiptSort.MOST_RECENT;

export interface SuggestedMatch {
  // Null on a ledger attach: the match came from the receipt's own column, not
  // a suggestion. Unlink routes by receipt uuid in that case.
  id: number | null;
  description: string | null;
  date: string | null;
  amount: string | null;
  delta: string | null;
}

export interface ReceiptListRow {
  uuid: string;
  status: string;
  merchant: string | null;
  transaction_date: string | null;
  amount: string | null;
  confidence_score: number;
  suggested_match: SuggestedMatch | null;
  no_match_reason?: "matcher_found_none" | "user_unlinked" | null;
  retry_expires_at?: string | null;
}

export interface ReceiptListCounts {
  pending: number;
  matched: number;
  unmatched: number;
}

export interface ReceiptsListResponse {
  receipts: ReceiptListRow[];
  meta: {
    total: number;
    counts: ReceiptListCounts;
  };
  links: {
    next: string | null;
    previous: string | null;
  };
}

export const RECEIPTS_DEFAULT_PAGE_SIZE = 25;

export interface UseReceiptsListOptions {
  bucket: ReceiptBucket;
  offset?: number;
  limit?: number;
  sort?: ReceiptSort;
  /** Skips the fetch entirely, e.g. for a user not yet known to have receipts access. */
  enabled?: boolean;
}

export interface UseReceiptsListResult {
  receipts: ReceiptListRow[];
  counts: ReceiptListCounts | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const RECEIPTS_QUERY_KEY = "receiptsList";
export const RECEIPTS_POLL_KEY = "poll";

// Phased upload-polling schedule. Extraction is never done before ~3s in
// observed runs, so the first fetch is deferred to UPLOAD_POLL_FIRST_DELAY_MS;
// subsequent fetches fire on UPLOAD_POLL_INTERVAL_MS; the schedule hard-stops
// at UPLOAD_POLL_TIMEOUT_MS so the modal can fall back to "still reading it".
export const UPLOAD_POLL_FIRST_DELAY_MS = 3_000;
export const UPLOAD_POLL_INTERVAL_MS = 1_000;
export const UPLOAD_POLL_TIMEOUT_MS = 30_000;

// Raw DB statuses (indigo.models.receipts.constants.ReceiptStatus) that the
// list endpoint actually returns. Polling stops as soon as the row reaches any
// of these — they are the union of every status the list-visible filter exposes.
// Earlier states (uploaded/extracting/matching) keep the row invisible, so
// polling will keep firing until extraction + matching resolve.
const TERMINAL_STATUSES = new Set([
  "awaiting_review",
  "confirmed",
  "no_match",
  "rejected",
  "orphaned",
  "extraction_failed",
]);

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "An unexpected error occurred";
}

export const useReceiptsList = (
  options: UseReceiptsListOptions
): UseReceiptsListResult => {
  const {
    bucket,
    offset = 0,
    limit = RECEIPTS_DEFAULT_PAGE_SIZE,
    sort,
    enabled = true,
  } = options;

  const params = new URLSearchParams();
  params.set("status", bucket);
  // Omit the default so unchanged URLs hit a single cached query key.
  if (sort && sort !== ReceiptSort.MOST_RECENT) params.set("sort", sort);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const path = `receipts/?${params.toString()}`;

  const { data, isLoading, error, refetch } = useQuery<ReceiptsListResponse>({
    queryKey: [
      RECEIPTS_QUERY_KEY,
      bucket,
      offset,
      limit,
      sort ?? ReceiptSort.MOST_RECENT,
    ],
    queryFn: async () => {
      const response = await ApiHttp.fetch<ReceiptsListResponse>(path);
      return response;
    },
    // Keep the prior page visible while the new offset/sort/bucket request
    // resolves, so the table doesn't flash an empty state during paging.
    placeholderData: keepPreviousData,
    retry: false,
    enabled,
  });

  const errorMessage: string | null = error ? extractErrorMessage(error) : null;

  return {
    receipts: data?.receipts ?? [],
    counts: data?.meta.counts ?? null,
    total: data?.meta.total ?? 0,
    isLoading,
    error: errorMessage,
    refetch: () => {
      refetch();
    },
  };
};

export type UploadPollingPhase = "reading" | "extracted" | "failed" | "timeout";

export interface UseReceiptUploadPollingResult {
  phase: UploadPollingPhase;
  row: ReceiptListRow | null;
  // Back-compat: true iff phase === "failed". ReceiptsPage uses this to fire
  // a failure toast as a belt-and-suspenders signal alongside the modal copy.
  isExtractionFailed: boolean;
  // Back-compat: true iff the polled row appeared in the list response, even
  // in a non-terminal state. ReceiptsPage uses this to gate list invalidation.
  isVisible: boolean;
}

// Polls the unfiltered receipts list for the just-uploaded receipt's uuid on a
// phased schedule (first fetch at t=3s, then 1s intervals, hard-stopping at
// 30s or terminal status). On terminal status, invalidates bucket-aware list
// queries so the row surfaces on whichever tab it landed on. Kept separate
// from useReceiptsList because the display query is per-bucket — polling
// against a bucket-filtered query would miss receipts that land elsewhere.
export const useReceiptUploadPolling = (
  pollForUuid: string | undefined
): UseReceiptUploadPollingResult => {
  const queryClient = useQueryClient();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  // React Query auto-fires the first fetch when `enabled` flips to true, so
  // we gate `enabled` on this flag to defer the initial fetch to t=3s without
  // having to schedule the first request ourselves.
  const [isFirstDelayElapsed, setIsFirstDelayElapsed] = useState(false);

  useEffect(() => {
    if (!pollForUuid) {
      setHasTimedOut(false);
      setIsFirstDelayElapsed(false);
      return undefined;
    }
    setHasTimedOut(false);
    setIsFirstDelayElapsed(false);
    const firstDelayHandle = setTimeout(() => {
      setIsFirstDelayElapsed(true);
    }, UPLOAD_POLL_FIRST_DELAY_MS);
    const timeoutHandle = setTimeout(() => {
      setHasTimedOut(true);
    }, UPLOAD_POLL_TIMEOUT_MS);
    return () => {
      clearTimeout(firstDelayHandle);
      clearTimeout(timeoutHandle);
    };
  }, [pollForUuid]);

  const { data } = useQuery<ReceiptsListResponse>({
    queryKey: [RECEIPTS_QUERY_KEY, RECEIPTS_POLL_KEY, pollForUuid],
    queryFn: () => ApiHttp.fetch<ReceiptsListResponse>("receipts/"),
    enabled: Boolean(pollForUuid) && isFirstDelayElapsed && !hasTimedOut,
    retry: false,
    refetchInterval: (query) => {
      if (!pollForUuid || hasTimedOut) return false;
      const latest = query.state.data as ReceiptsListResponse | undefined;
      const polled = latest?.receipts.find((r) => r.uuid === pollForUuid);
      if (polled && TERMINAL_STATUSES.has(polled.status)) return false;
      return UPLOAD_POLL_INTERVAL_MS;
    },
  });

  const polledRow = pollForUuid
    ? data?.receipts.find((r) => r.uuid === pollForUuid) ?? null
    : null;

  const phase: UploadPollingPhase = useMemo(() => {
    if (polledRow && TERMINAL_STATUSES.has(polledRow.status)) {
      return polledRow.status === "extraction_failed" ? "failed" : "extracted";
    }
    if (hasTimedOut) return "timeout";
    return "reading";
  }, [polledRow, hasTimedOut]);

  const isTerminalPhase = phase === "extracted" || phase === "failed";

  useEffect(() => {
    if (!isTerminalPhase) return;
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey[0] === RECEIPTS_QUERY_KEY &&
        q.queryKey[1] !== RECEIPTS_POLL_KEY,
    });
  }, [isTerminalPhase, queryClient]);

  return {
    phase,
    row: isTerminalPhase ? polledRow : null,
    isExtractionFailed: phase === "failed",
    isVisible: Boolean(polledRow),
  };
};
